import { createApi } from '@reduxjs/toolkit/query/react';
import { Language } from '@shared/enum';
import { apiBaseQuery } from '@shared/functions';

export type ErrorObject = {
    [key: string]: string;
};

export interface Lesson {
    id: number;
    name: string;
}
export interface Course {
    id: number;
    name: string;
}
export interface Program {
    id: number;
    name: string;
}
export interface Assignment {
    id: number;
    name: string;
}
export interface User {
    user_id: number;
    full_name: string;
    group_id: number[];
}

export interface Group {
    group_id: number;
    name: string;
}

export interface KPIs {
    users: number;
    avg_time: number;
    avg_score: number;
    completion_rate: number;
}

export interface DriverDetails {
    user_id: number;
    user_full_name: string;
    language: string;
    completion_prct: number;
    user_lesson_completion_rate: number;
    lesson_name: string;
    lesson_type: string;
    lesson_mode: string;
    total_score: number;
    lesson_attempt_id: number;
    started_at: string;
    session_duration: string;
    attempt_asc: number;
    attempt_desc: number;
    attempts: number;
    lesson_attempt_status_id: number;
    lesson_attempt_status_name: string;
    course_lesson_id: number;
    lesson_id: number;
    current_simulator_id?: number | null;
    note?: string | null;
    assignment_user_lesson_id?: number | null;
    assignment_id: number | null;
    lesson_custom_config?: string | null;
}

export interface DriverDetailsUI {
    current_simulator_id?: number | null;
    lesson_attempt_status_id: number;
    lesson_attempt_status_name: string;
}
export interface DriverDetailsUI2 {
    current_simulator_id?: number | null;
}

export interface CustomLessonConfig {
    simulationType?: string;
    lessonLanguage?: string;
    aiTraffic?: string;
    timeOfDay?: string;
    weather?: string;
    vrTutorial?: string;
}

export interface FilterValuesRequest {
    language: Language;
}

export interface FilterValuesResponse {
    status: number;
    message: string;
    data: {
        users: User[];
        groups: Group[];
        lessons: Lesson[];
        courses: Course[];
        programs: Program[];
        assignments: Assignment[];
    };
    error: ErrorObject;
}

export interface MainKPIsRequest {
    language: Language;
    user_id: string[];
    group_id: string[];
    lesson_id: string[];
    assignment_id: string[];
    score_from?: number;
    score_to?: number;
    period: number;
    last_attempt?: boolean;
    only_exams?: boolean;
    random: number;
}

export interface MainKPIsResponse {
    status: number;
    message: string;
    data: KPIs;
    error: ErrorObject;
}

export interface DriverDetailsRequest {
    language: Language;
    user_id: string[];
    group_id: string[];
    lesson_id: string[];
    assignment_id: string[];
    score_from?: number;
    score_to?: number;
    period: number;
    last_attempt?: boolean;
    only_exams?: boolean;
    random: number;
}

export interface DriverDetailsResponse {
    status: number;
    message: string;
    data: DriverDetails[];
    error: ErrorObject;
}

export interface DriverDetailsExportRequest {
    language: Language;
    user_id: string[];
    group_id: string[];
    lesson_id: string[];
    assignment_id: string[];
    score_from?: number;
    score_to?: number;
    period: number;
    last_attempt?: boolean;
    only_exams?: boolean;
}

export const performanceApi = createApi({
    reducerPath: 'performanceApi',
    baseQuery: apiBaseQuery,
    tagTypes: ['FilterValues', 'MainKPIs', 'DriverDetails'],
    endpoints: (builder) => ({
        getFilterValues: builder.query<FilterValuesResponse, FilterValuesRequest>({
            query: (data) => ({
                url: 'v1/performance/filter_values',
                params: {
                    language: data.language.toLowerCase(),
                },
                method: 'GET',
                credentials: 'include',
            }),
            providesTags: ['FilterValues'],
        }),
        getMainKPIs: builder.query<MainKPIsResponse, MainKPIsRequest>({
            query: (data) => ({
                url: 'v1/performance/kpis',
                params: {
                    language: data.language.toLowerCase(),
                    user_id: data.user_id,
                    group_id: data.group_id,
                    lesson_id: data.lesson_id,
                    assignment_id: data.assignment_id,
                    score_from: data.score_from,
                    score_to: data.score_to,
                    period: data.period,
                    last_attempt: data.last_attempt,
                    only_exams: data.only_exams,
                    r: data.random,
                },
                method: 'GET',
                credentials: 'include',
            }),
            providesTags: ['MainKPIs'],
        }),
        getDriverDetails: builder.query<DriverDetailsResponse, DriverDetailsRequest>({
            query: (data) => ({
                url: 'v1/performance/driver_details',
                params: {
                    language: data.language.toLowerCase(),
                    user_id: data.user_id,
                    group_id: data.group_id,
                    lesson_id: data.lesson_id,
                    assignment_id: data.assignment_id,
                    score_from: data.score_from,
                    score_to: data.score_to,
                    period: data.period,
                    last_attempt: data.last_attempt,
                    only_exams: data.only_exams,
                    r: data.random,
                },
                method: 'GET',
                credentials: 'include',
            }),
            providesTags: ['DriverDetails'],
        }),
    }),
});

export const { useGetFilterValuesQuery, useGetMainKPIsQuery, useGetDriverDetailsQuery } = performanceApi;
